/*
 * The Once product page (sections/once-product.liquid).
 *
 * <once-product> owns the shopper's choices and the variant they add up to. The
 * blocks inside it are plain markup; this file reads them and keeps them in step:
 *
 * - Colour swatches and the size picker set options; the matching variant goes in
 *   the form's hidden id, and the price, gallery photo and button follow it.
 * - The size picker has two modes. "kit" orders the size value that means "not
 *   chosen yet" and adds the free sizing kit with it. "size" needs a real size
 *   first. The block resolves which value that is; if it can't, the picker
 *   falls back to asking for a size.
 * - The batch dropdown opens on click; a second click, Esc or a click outside
 *   closes it.
 * - The gallery swipes, and its thumbnails (or, on phones, its dots) follow it.
 * - The sticky bar at the bottom shows once the pre-order button has scrolled
 *   off the top of the screen; its Continue scrolls back to the options.
 * - Add to cart goes through the Cart API and then announces itself the way
 *   Horizon's own product form does, so the cart drawer and count update as usual.
 *
 * Every listener is delegated from <once-product>, so blocks the theme editor
 * re-renders on their own keep working without being re-bound.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class OnceProduct extends HTMLElement {
  /** @type {{ options: string[], variants: Array<{ id: number, options: string[], available: boolean, price: string, compare_at_price: string | null, discount: number, saving: string, media_id: number | null }> }} */
  data = { options: [], variants: [] };

  /** @type {string[]} */
  selection = [];

  /** 'kit' or 'size'; only meaningful when the page has a size picker. */
  sizeMode = 'kit';

  /** @type {string | null} */
  pickedSize = null;

  connectedCallback() {
    const json = this.querySelector('[data-once-product-json]');
    if (!json) return;
    this.data = JSON.parse(json.textContent || '{}');

    const startId = this.querySelector('[data-once-variant-id]')?.value;
    const start = this.data.variants.find((v) => String(v.id) === String(startId)) ?? this.data.variants[0];
    if (!start) return;
    this.selection = [...start.options];

    /* A link with ?variant= for a real size opens with that size chosen. Anything
       else starts on the sizing kit, which is what most shoppers need. */
    const size = this.#sizeConfig();
    if (size) {
      const fromLink = new URL(window.location.href).searchParams.has('variant');
      const startSize = this.selection[size.index];
      if (!this.#kitIsSellable(size)) {
        /* The block couldn't match its kit value to a real variant, so there's
           nothing to order in kit mode. Ask for a size instead of leaving the
           page on a variant that doesn't exist. */
        this.sizeMode = 'size';
        this.pickedSize = fromLink ? startSize : null;
      } else if (fromLink && startSize !== size.kitValue) {
        this.sizeMode = 'size';
        this.pickedSize = startSize;
      } else {
        this.selection[size.index] = size.kitValue;
      }
    }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);

    this.#initGallery();
    this.#initSticky();
    this.#render({ initial: true });

    /* Browsers restore radio state on back/forward after scripts run, which can
       leave a swatch checked that no longer matches the selection. */
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    this.#stickyObserver?.disconnect();
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #onPageShow = () => this.#render({ initial: true });

  /* ---------------------------------------------------------------- options */

  #sizeConfig() {
    const root = this.querySelector('[data-once-size]');
    if (!(root instanceof HTMLElement)) return null;
    const index = this.data.options.indexOf(root.dataset.optionName ?? '');
    if (index < 0) return null;
    return {
      root,
      index,
      kitValue: root.dataset.kitValue ?? '',
      kitVariantId: Number(root.dataset.kitVariantId) || null,
    };
  }

  /**
   * Whether the kit value the block resolved is a size this product actually has.
   * @param {NonNullable<ReturnType<OnceProduct['#sizeConfig']>>} size
   */
  #kitIsSellable(size) {
    if (!size.kitValue) return false;
    return this.data.variants.some((v) => v.options[size.index] === size.kitValue);
  }

  get variant() {
    return this.data.variants.find((v) => v.options.every((value, i) => value === this.selection[i])) ?? null;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.onceOption) return;
    const index = this.data.options.indexOf(input.dataset.onceOption);
    if (index < 0) return;
    this.selection[index] = input.value;
    this.#render();
  };

  /** @param {'kit' | 'size'} mode */
  #setSizeMode(mode) {
    const size = this.#sizeConfig();
    if (!size) return;
    this.sizeMode = mode;
    /* kitValue is empty when the block couldn't resolve one; keep whatever size
       is already selected rather than clearing it to nothing. */
    this.selection[size.index] =
      mode === 'kit' ? size.kitValue : this.pickedSize ?? (size.kitValue || this.selection[size.index]);
    this.#render();
  }

  /* ------------------------------------------------------------------ clicks */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const mode = target.closest('[data-once-size-mode]');
    if (mode instanceof HTMLElement) {
      this.#setSizeMode(mode.dataset.onceSizeMode === 'size' ? 'size' : 'kit');
      return;
    }

    const sizeValue = target.closest('[data-once-size-value]');
    if (sizeValue instanceof HTMLElement) {
      this.pickedSize = sizeValue.dataset.onceSizeValue ?? null;
      this.#setSizeMode('size');
      return;
    }

    const trigger = target.closest('[data-once-batch-trigger]');
    if (trigger) {
      const batch = trigger.closest('[data-once-batch]');
      if (batch instanceof HTMLElement) {
        this.#setBatchOpen(batch, !batch.hasAttribute('data-open'));
      }
      return;
    }

    const thumb = target.closest('[data-once-gallery-thumb], [data-once-gallery-dot]');
    if (thumb instanceof HTMLElement) {
      this.#goToSlide(Number(thumb.dataset.onceGalleryThumb ?? thumb.dataset.onceGalleryDot));
      return;
    }

    if (target.closest('[data-once-sticky-continue]')) {
      const options = this.querySelector('.once-color, [data-once-size], [data-once-buy]');
      options?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const guideOpen = target.closest('[data-once-size-guide-open]');
    if (guideOpen) {
      const dialog = this.querySelector('[data-once-size-guide]');
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
      return;
    }

    const dialog = target.closest('[data-once-size-guide]');
    if (dialog instanceof HTMLDialogElement) {
      /* A click on the backdrop lands on the <dialog> itself, not its contents. */
      if (target === dialog || target.closest('[data-once-size-guide-close]')) dialog.close();
    }
  };

  /* --------------------------------------------------------- batch dropdown */

  /**
   * @param {HTMLElement} batch
   * @param {boolean} open
   */
  #setBatchOpen(batch, open) {
    batch.toggleAttribute('data-open', open);
    batch.querySelector('[data-once-batch-trigger]')?.setAttribute('aria-expanded', String(open));
  }

  /**
   * The dropdown is the trigger and the panel, not the whole rating row, so a
   * click on the stars counts as outside and closes it.
   * @param {EventTarget | null} target
   */
  #batchZone(target) {
    const zone = target instanceof Element ? target.closest('[data-once-batch-trigger], [data-once-batch-panel]') : null;
    const batch = zone?.closest('[data-once-batch]');
    return batch instanceof HTMLElement ? batch : null;
  }

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    const inside = this.#batchZone(event.target);
    for (const batch of this.querySelectorAll('[data-once-batch][data-open]')) {
      if (batch instanceof HTMLElement && batch !== inside) this.#setBatchOpen(batch, false);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const batch = event.target instanceof Element ? event.target.closest('[data-once-batch][data-open]') : null;
    if (!(batch instanceof HTMLElement)) return;
    this.#setBatchOpen(batch, false);
    const trigger = batch.querySelector('[data-once-batch-trigger]');
    if (trigger instanceof HTMLElement) trigger.focus();
  };

  /* ---------------------------------------------------------------- gallery */

  get #track() {
    return this.querySelector('[data-once-gallery-track]');
  }

  #initGallery() {
    const gallery = this.querySelector('[data-once-gallery]');
    const track = this.#track;
    if (!(gallery instanceof HTMLElement) || !(track instanceof HTMLElement)) return;
    this.#addExtraSlideNav(gallery, track);
    const index = Number(gallery.dataset.startIndex) || 0;
    if (index > 0) track.scrollLeft = index * track.clientWidth;
    this.#syncThumbs(index);
  }

  /* Slides added as blocks inside the gallery (blocks/_once-gallery-image-slide
     and _once-gallery-app-slide) are invisible to the gallery's Liquid, so their
     thumbnails and dots are made here from each slide's data-thumb attributes.
     Then every slide, thumbnail and dot is numbered "n / total" again. Safe to
     run twice: a thumbnail or dot that already exists is left alone. */

  /**
   * @param {HTMLElement} gallery
   * @param {HTMLElement} track
   */
  #addExtraSlideNav(gallery, track) {
    const slides = [...track.children].filter((slide) => slide instanceof HTMLElement);
    const thumbs = gallery.querySelector('.once-gallery__thumbs');
    const dots = gallery.querySelector('.once-gallery__dots');

    slides.forEach((slide, i) => {
      if (!(slide instanceof HTMLElement) || !slide.hasAttribute('data-once-gallery-extra')) return;

      if (thumbs && !thumbs.querySelector(`[data-once-gallery-thumb="${i}"]`)) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'once-gallery__thumb';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', 'false');
        button.dataset.onceGalleryThumb = String(i);
        button.style.background = slide.dataset.thumbBg ?? '';
        if (slide.dataset.thumb) {
          const img = document.createElement('img');
          img.src = slide.dataset.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.style.objectFit = slide.dataset.thumbFit ?? 'cover';
          img.style.objectPosition = slide.dataset.thumbPosition ?? '50% 50%';
          button.append(img);
        }
        thumbs.append(button);
      }

      if (dots && !dots.querySelector(`[data-once-gallery-dot="${i}"]`)) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'once-gallery__dot';
        dot.dataset.onceGalleryDot = String(i);
        dots.append(dot);
      }
    });

    const total = slides.length;
    const label = (/** @type {Element} */ el, /** @type {number} */ i) => el.setAttribute('aria-label', `${i + 1} / ${total}`);
    slides.forEach(label);
    thumbs?.querySelectorAll('[data-once-gallery-thumb]').forEach(label);
    dots?.querySelectorAll('[data-once-gallery-dot]').forEach(label);
    if (thumbs instanceof HTMLElement) thumbs.hidden = total < 2;
    if (dots instanceof HTMLElement) dots.hidden = total < 2;
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #goToSlide(index, behavior = 'smooth') {
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;
    track.scrollTo({ left: index * track.clientWidth, behavior });
    this.#syncThumbs(index);
  }

  /** @type {number | undefined} */
  #scrollFrame;

  /** @param {Event} event */
  #onScroll = (event) => {
    if (event.target !== this.#track) return;
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      const track = this.#track;
      if (!(track instanceof HTMLElement) || !track.clientWidth) return;
      this.#syncThumbs(Math.round(track.scrollLeft / track.clientWidth));
    });
  };

  /** @param {number} index */
  #syncThumbs(index) {
    this.querySelectorAll('[data-once-gallery-dot]').forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    const thumbs = this.querySelectorAll('[data-once-gallery-thumb]');
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-selected', String(i === index)));

    /* Scroll the thumbnail strip itself, never the page, so an off-screen
       thumbnail comes into view without the window jumping. */
    const active = thumbs[index];
    const strip = active?.parentElement;
    if (!(active instanceof HTMLElement) || !strip) return;
    const start = active.offsetLeft - strip.offsetLeft;
    if (start < strip.scrollLeft || start + active.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: start, behavior: 'smooth' });
    }
  }

  /** @param {number | null} mediaId */
  #showMedia(mediaId) {
    if (!mediaId) return;
    /* Count every slide, not just the product's photos: the lead image comes
       first and has no media id. */
    const slides = [...(this.#track?.children ?? [])];
    const index = slides.findIndex((slide) => slide instanceof HTMLElement && slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.#goToSlide(index);
  }

  /* ------------------------------------------------------------- sticky bar */

  /** @type {IntersectionObserver | undefined} */
  #stickyObserver;

  /* Shown only once the pre-order button is above the screen, i.e. the visitor
     has scrolled past it, not while they simply haven't reached it yet. */
  #initSticky() {
    const bar = this.querySelector('[data-once-sticky]');
    const button = this.querySelector('[data-once-buy-button]');
    if (!(bar instanceof HTMLElement) || !button) return;

    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      bar.toggleAttribute('data-shown', passed);
      bar.setAttribute('aria-hidden', String(!passed));
      bar.querySelector('[data-once-sticky-continue]')?.setAttribute('tabindex', passed ? '0' : '-1');
    });
    this.#stickyObserver.observe(button);
  }

  /* ----------------------------------------------------------------- render */

  /** @param {{ initial?: boolean }} [options] */
  #render({ initial = false } = {}) {
    const previous = this.querySelector('[data-once-variant-id]')?.value;
    const variant = this.variant;
    const size = this.#sizeConfig();
    const incomplete = Boolean(size && this.sizeMode === 'size' && !this.pickedSize);

    const idInput = this.querySelector('[data-once-variant-id]');
    if (idInput instanceof HTMLInputElement) idInput.value = variant ? String(variant.id) : '';

    this.#renderOptions();
    if (size) this.#renderSize(size, incomplete);
    if (variant) this.#renderPrice(variant);
    this.#renderButton(variant, incomplete);

    if (!initial && variant && String(variant.id) !== previous) {
      const before = this.data.variants.find((v) => String(v.id) === previous);
      if (variant.media_id !== before?.media_id) this.#showMedia(variant.media_id);

      if (this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url);
      }
    }
  }

  #renderOptions() {
    for (const input of this.querySelectorAll('input[data-once-option]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const index = this.data.options.indexOf(input.dataset.onceOption ?? '');
      if (index < 0) continue;
      input.checked = this.selection[index] === input.value;
      input.closest('label')?.toggleAttribute('data-unavailable', !this.#isAvailable(index, input.value));
    }

    for (const label of this.querySelectorAll('[data-once-option-value]')) {
      if (!(label instanceof HTMLElement)) continue;
      const index = this.data.options.indexOf(label.dataset.onceOptionValue ?? '');
      if (index >= 0) label.textContent = this.selection[index] ?? '';
    }
  }

  /**
   * Whether picking `value` for option `index`, keeping the other choices, lands on
   * a variant that can be bought.
   * @param {number} index
   * @param {string} value
   */
  #isAvailable(index, value) {
    /* When nothing at all can be bought, the button already says so; greying out
       every swatch and size on top of that would only look broken. */
    if (!this.data.variants.some((v) => v.available)) return true;
    return this.data.variants.some(
      (v) => v.available && v.options.every((option, i) => (i === index ? option === value : option === this.selection[i]))
    );
  }

  /**
   * @param {NonNullable<ReturnType<OnceProduct['#sizeConfig']>>} size
   * @param {boolean} incomplete
   */
  #renderSize(size, incomplete) {
    const { root, index } = size;
    root.dataset.mode = this.sizeMode;
    root.toggleAttribute('data-incomplete', incomplete);

    for (const button of root.querySelectorAll('[data-once-size-mode]')) {
      if (!(button instanceof HTMLElement)) continue;
      const isSize = button.dataset.onceSizeMode === 'size';
      /* The size choice only reads as picked once a size exists. */
      const pressed = isSize ? this.sizeMode === 'size' && Boolean(this.pickedSize) : this.sizeMode === 'kit';
      button.setAttribute('aria-pressed', String(pressed));
      if (isSize) button.setAttribute('aria-expanded', String(this.sizeMode === 'size'));
    }

    for (const button of root.querySelectorAll('[data-once-size-value]')) {
      if (!(button instanceof HTMLElement)) continue;
      const value = button.dataset.onceSizeValue ?? '';
      button.setAttribute('aria-pressed', String(this.sizeMode === 'size' && value === this.pickedSize));
      button.toggleAttribute('data-unavailable', !this.#isAvailable(index, value));
    }
  }

  /** @param {NonNullable<OnceProduct['variant']>} variant */
  #renderPrice(variant) {
    for (const block of this.querySelectorAll('[data-once-price-block]')) {
      if (!(block instanceof HTMLElement)) continue;
      const row = block.querySelector('.once-price__row');
      const price = block.querySelector('[data-once-price]');
      const compare = block.querySelector('[data-once-compare]');
      const discount = block.querySelector('[data-once-discount]');
      if (price) price.textContent = variant.price;
      if (compare) compare.textContent = variant.compare_at_price ?? '';
      if (discount) {
        discount.textContent = (block.dataset.discountFormat ?? '')
          .replace('[percent]', String(variant.discount))
          .replace('[amount]', variant.saving ?? '');
      }
      row?.setAttribute('data-on-sale', String(Boolean(variant.compare_at_price)));
    }

    const stickyPrice = this.querySelector('[data-once-sticky-price]');
    const stickyCompare = this.querySelector('[data-once-sticky-compare]');
    if (stickyPrice) stickyPrice.textContent = variant.price;
    if (stickyCompare instanceof HTMLElement) {
      stickyCompare.textContent = variant.compare_at_price ?? '';
      stickyCompare.hidden = !variant.compare_at_price;
    }
  }

  /**
   * @param {OnceProduct['variant']} variant
   * @param {boolean} incomplete
   */
  #renderButton(variant, incomplete) {
    const buy = this.querySelector('[data-once-buy]');
    if (!(buy instanceof HTMLElement)) return;
    const button = buy.querySelector('[data-once-buy-button]');
    const label = buy.querySelector('[data-once-buy-label]');

    let text = buy.dataset.label;
    let disabled = false;
    if (incomplete) {
      text = buy.dataset.incompleteLabel;
      disabled = true;
    } else if (!variant) {
      text = buy.dataset.unavailableLabel;
      disabled = true;
    } else if (!variant.available) {
      text = buy.dataset.soldOutLabel;
      disabled = true;
    }

    if (label) label.textContent = text ?? '';
    if (button instanceof HTMLButtonElement) button.disabled = disabled;

    const price = buy.querySelector('[data-once-buy-price]');
    if (price instanceof HTMLElement) {
      price.hidden = disabled;
      const current = price.querySelector('[data-once-buy-current]');
      const compare = price.querySelector('[data-once-buy-compare]');
      if (variant && current) current.textContent = variant.price;
      if (variant && compare instanceof HTMLElement) {
        compare.textContent = variant.compare_at_price ?? '';
        compare.hidden = !variant.compare_at_price;
      }
    }
  }

  /* ------------------------------------------------------------ add to cart */

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-once-product-form]')) return;
    event.preventDefault();

    const buy = this.querySelector('[data-once-buy]');
    const button = this.querySelector('[data-once-buy-button]');
    const error = this.querySelector('[data-once-buy-error]');
    const variant = this.variant;
    if (!variant || !(button instanceof HTMLButtonElement) || button.disabled) return;
    if (buy?.hasAttribute('data-loading')) return;

    const items = [{ id: variant.id, quantity: 1 }];
    const size = this.#sizeConfig();
    if (size?.kitVariantId && this.sizeMode === 'kit') items.push({ id: size.kitVariantId, quantity: 1 });

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    /* The same announcement Horizon's product form makes: the cart drawer opens
       on it, and the header count and drawer contents update when it resolves. */
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    buy?.setAttribute('data-loading', '');
    if (error instanceof HTMLElement) error.hidden = true;

    const routes = window.Theme?.routes ?? {};
    const detail = { source: 'once-product', productId: this.dataset.productId, itemCount: items.length };

    try {
      const response = await fetch(routes.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      });
      const result = await response.json();
      const cart = await fetch(`${routes.cart_url ?? '/cart'}.js`, { headers: { Accept: 'application/json' } }).then(
        (r) => r.json()
      );

      if (result.status) {
        this.#showError(result.description || result.message || buy?.dataset.errorLabel);
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, didError: true, items: cart.items },
        });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, didError: false, items: cart.items, sections: result.sections },
      });

      this.#afterAdd(buy?.dataset.afterAdd);
    } catch (err) {
      console.error(err);
      deferred.reject(err);
      this.#showError(buy?.dataset.errorLabel);
    } finally {
      buy?.removeAttribute('data-loading');
    }
  };

  /**
   * The store's own "open the drawer on add" setting is off and the header has no
   * cart icon, so the drawer is opened here rather than relying on it.
   * @param {string | undefined} destination
   */
  #afterAdd(destination) {
    if (destination === 'checkout') {
      window.location.assign('/checkout');
    } else if (destination === 'cart') {
      window.location.assign(window.Theme?.routes?.cart_url ?? '/cart');
    } else {
      const drawer = /** @type {(HTMLElement & { open?: () => void, isOpen?: boolean }) | null} */ (
        document.getElementById('cart-drawer')
      );
      if (drawer?.open && !drawer.isOpen) drawer.open();
    }
  }

  /** @param {string | undefined} message */
  #showError(message) {
    const error = this.querySelector('[data-once-buy-error]');
    if (!(error instanceof HTMLElement) || !message) return;
    error.textContent = message;
    error.hidden = false;
  }
}

if (!customElements.get('once-product')) customElements.define('once-product', OnceProduct);
